import { FormEvent, Dispatch, SetStateAction } from 'react';
import {
  Box,
  FormControl,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  ModalOverlay,
} from '@chakra-ui/react';
import { Button } from 'components/elements/Button';
import { Label } from 'components/elements/Label';
import { Text } from 'components/elements/Text';
import { CrossIcon } from 'components/icons/CrossIcon';
import { LinkIcon } from 'components/icons/LinkIcon';
import { FlexColumn } from 'components/layout/FlexColumn/FlexColumn';
import { FlexRow } from 'components/layout/FlexRow';
import { useToast } from 'hooks/useToast';
import { EVENT_NAMES } from 'services/analytics/eventNames';
import { trackEvent } from 'services/analytics/trackEvent';
import { cloneSavedSearchUrl } from 'utils/navigation';

type ShareGroupConfigProps = {
  isShareModalOpen: boolean;
  id: string;
  setIsShareModalOpen: Dispatch<SetStateAction<boolean>>;
  onCancelClick: () => void;
};

export const ShareGroupConfig = ({
  isShareModalOpen,
  setIsShareModalOpen,
  onCancelClick,
  id,
}: ShareGroupConfigProps) => {
  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
  };
  const { renderCustomToast: toast } = useToast();
  const isCompaniesPage = window.location.pathname.includes('companies');
  const cloneLink = cloneSavedSearchUrl(id);

  return (
    <Modal
      isOpen={isShareModalOpen}
      onClose={() => setIsShareModalOpen(!isShareModalOpen)}
    >
      <ModalOverlay sx={{ backdropFilter: 'blur(2rem)' }} />
      <ModalContent
        sx={{
          borderRadius: '8px',
          boxSizing: 'border-box',
        }}
      >
        <FlexColumn
          sx={{ height: '100%' }}
          justifyContent="flex-start"
          alignItems="center"
        >
          <ModalHeader sx={{ width: '100%', margin: '0px', padding: '17px 16px 0px' }}>
            <FlexRow alignItems="center" justifyContent="space-between">
              <Text size="lg" sx={{ fontSize: '20px' }} fontWeight={700}>
                Share saved search
              </Text>
              <Box
                sx={{ cursor: 'pointer' }}
                onClick={() => setIsShareModalOpen(!isShareModalOpen)}
              >
                <CrossIcon stroke="#2C402E" opacity={0.2} />
              </Box>
            </FlexRow>
          </ModalHeader>
          <ModalBody sx={{ width: '100%', padding: '24px 16px' }}>
            {/* TODO: react-hook-form refactor once we have filters using the form library */}
            <form onSubmit={handleSubmit}>
              <FormControl sx={{ width: '100%', marginBottom: '24px' }}>
                <Box>
                  <Text>Share this with someone by sending them the link below.</Text>
                  <Text sx={{ fontWeight: '700', mt: '15px' }}>
                    NOTE: User must be logged in to for link to take affect.
                  </Text>
                  <Label
                    sx={{
                      textTransform: 'none',
                      fontSize: '12px',
                      ml: '6px',
                      mt: '20px',
                    }}
                  >
                    Share link
                  </Label>
                  <Input
                    value={`${window.location.origin}${cloneLink}`}
                    type="text"
                    variant="outlined"
                    disabled
                    sx={{
                      bgColor: 'transparent',
                      marginTop: '4px',
                      height: '50px',
                      fontSize: '14px',
                      fontWeight: '600',
                    }}
                  />
                </Box>
              </FormControl>
              <Box
                sx={{
                  color: 'brand.army-green',
                  mb: '23px',
                  display: 'inline-flex',
                  cursor: 'pointer',
                }}
                onClick={() => {
                  navigator.clipboard.writeText(`${window.location.origin}${cloneLink}`);
                  // Toast success message
                  toast({
                    title: 'Copied to clipboard',
                    status: 'success',
                  });
                  trackEvent(EVENT_NAMES.SHARE, {
                    groupId: id,
                    groupType: isCompaniesPage ? 'company_group' : 'project_group',
                  });
                }}
              >
                <LinkIcon />
                <Text
                  sx={{
                    color: 'brand.army-green',
                    ml: '10px',
                  }}
                >
                  Copy to clipboard
                </Text>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                }}
              >
                <Button
                  color="secondary"
                  onClick={onCancelClick}
                  sx={{ mr: '8px', p: '16px', height: 'fit-content' }}
                >
                  Close
                </Button>
              </Box>
            </form>
          </ModalBody>
        </FlexColumn>
      </ModalContent>
    </Modal>
  );
};
